import { useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Table,
  Button,
  Flex,
  Badge,
} from "@radix-ui/themes";
import { ArrowLeftIcon, GearIcon, PlayIcon } from "@radix-ui/react-icons";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { Section } from "@radix-ui/themes";
import { useReleases } from "@/routes/simulator/context/release";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import styles from "./RegressionSetTests.module.scss";
import { toast } from "sonner";
import { ReleaseStatus } from "@/v2/entities/release/Release.model";
import { RatioFormatter } from "@/v2/components/formatters/RationFormatter";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useMutation, useQuery } from "react-query";
import { RegressionBreadcrumbs } from "./RegressionBreadcrumbs";
import { StructureCheck } from "@/v2/assets/icons/StructureCheck";
import { formatRegressionRunMethodLabel } from "@/routes/regression-tests/regressionTestRunMethodLabel";

export function RegressionSetTests() {
  const { agentId, setId } = useParams();
  const navigate = useNavigate();
  const { simulators } = useReleases();
  const {
    getRegressionSet,
    getRegressionTestsByRegressionSetId,
    runRegressionTestBySet,
  } = useRegressionApi();

  const agent = useMemo(() => {
    return simulators?.find((s) => s.id.toString() === agentId);
  }, [simulators, agentId]);

  const release = useMemo(() => {
    return agent?.releases.find((r) => r.status === ReleaseStatus.published);
  }, [agent]);

  const { data: setInfo } = useQuery({
    queryKey: ["regression-set", setId],
    queryFn: () => getRegressionSet(setId || ""),
  });

  const { data: tests, refetch: refetchTests } = useQuery({
    queryKey: ["regression-tests", setId],
    queryFn: () => getRegressionTestsByRegressionSetId(setId || ""),
  });

  const runTestMutation = useMutation({
    mutationKey: ["run-regression-test-by-set"],
    mutationFn: runRegressionTestBySet,
  });

  const handleRunManually = useCallback(async () => {
    try {
      const result = await runTestMutation.mutateAsync({
        regressionSetId: setId || "",
        releaseId: release?.id || 0,
      });

      toast.success(`Test ${result.regressionTestId} completed`);
      refetchTests();
    } catch (error) {
      toast.error("Failed to run regression test");
    }
  }, [setId, runTestMutation, release, refetchTests]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);


  const getAccuracyColor = useCallback((accuracy: number | null) => {
    if (!accuracy) return "gray";
    if (accuracy >= 0.8) return "green";
    if (accuracy >= 0.8) return "yellow";
    return "red";
  }, []);

  if (!agent) {
    return (
      <PageLayoutV2>
        <Flex justify="center" align="center" style={{ minHeight: "400px" }}>
          <Text size="3" color="gray">
            Agent not found
          </Text>
        </Flex>
      </PageLayoutV2>
    );
  }

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <RegressionBreadcrumbs
          agentId={agentId}
          agentName={agent.name}
          agentChannel={agent.channel}
          regressionSetName={setInfo?.name}
          regressionSetId={setId}
        />
        <PageHeader
          title={`Test: ${setInfo?.name}`}
          description={`Test runs for "${agent.name}" agent`}
          icon={<StructureCheck size={32} fill="gray" />}
          actions={
            <Flex gap="3" align="center">
              <Button
                variant="outline"
                size="2"
                highContrast
                onClick={() =>
                  navigate(
                    `/training-center/${agentId}/regression-sets/${setId}/configure`
                  )
                }
              >
                <GearIcon />
                Configure
              </Button>
              <Button
                variant="outline"
                size="2"
                highContrast
                onClick={handleRunManually}
                disabled={runTestMutation.isLoading}
              >
                <PlayIcon />
                Run Manually
              </Button>
            </Flex>
          }
        />

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="6" width="100%">
            {/* Test Runs Table */}
            <Box>
              <Flex direction="column" gap="4">
                <Flex direction={"row"} gap={"2"} align="center">
                  <Flex gap={"2"} align={"center"}>
                    <Text size={"3"} weight={"medium"}>
                      Test Runs
                    </Text>
                    <Badge color="gray">{tests?.length ?? 0}</Badge>
                  </Flex>
                </Flex>

                <Table.Root>
                  <Table.Header>
                    <Table.Row className=" text-gray-500 font-normal">
                      <Table.ColumnHeaderCell className=" font-normal">
                        Run ID
                      </Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell className=" font-normal">
                        Date
                      </Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell className=" font-normal">
                        Test Type
                      </Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell className=" font-normal">
                        Accuracy Rate
                      </Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell className=" font-normal">
                        Items Checked
                      </Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {tests?.map((test) => (
                      <Table.Row
                        key={test.id}
                        className={styles.tableRow}
                        onClick={() =>
                          navigate(
                            `/training-center/${agentId}/regression-sets/${setId}/tests/${test.id}`
                          )
                        }
                      >
                        <Table.Cell>
                          <Text weight="medium">{test.id}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text size="2" color="gray">
                            {formatDate(test.createdAt)}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge size="2" color="blue">
                            {formatRegressionRunMethodLabel(test.runMethod)}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge
                            size="2"
                            color={getAccuracyColor(test.avgAccuracy)}
                          >
                            {RatioFormatter({
                              value: test.avgAccuracy,
                            })}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Text>{test.numberOfResults}</Text>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Flex>
            </Box>
          </Flex>
        </Section>
      </Flex>
    </PageLayoutV2>
  );
}
